import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// Adelaide timezone constant
const ADELAIDE_TIMEZONE = 'Australia/Adelaide';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Converts a Date object or date string to Adelaide timezone and returns YYYY-MM-DD format.
 * This is the critical function for date comparisons - ensures all dates are compared in Adelaide time.
 * 
 * IMPORTANT: Handles both ISO strings (with Z or timezone) and local datetime strings (without timezone).
 * For local datetime strings like "2026-01-19T06:00", we assume they're already in Adelaide time.
 */
export const toAdelaideDateString = (dateInput) => {
  if (!dateInput) return '';
  
  // If it's a string, check if it has timezone info
  if (typeof dateInput === 'string') {
    // If the string has Z (UTC) or a timezone offset, parse normally and convert
    if (dateInput.includes('Z') || dateInput.includes('+') || /T\d{2}:\d{2}:\d{2}[+-]/.test(dateInput)) {
      const date = new Date(dateInput);
      return date.toLocaleDateString('en-CA', { timeZone: ADELAIDE_TIMEZONE });
    }
    
    // For local datetime strings like "2026-01-19T06:00" or "2026-01-19T15:00",
    // they're already in Adelaide local time, so just extract the date part
    const match = dateInput.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) {
      return match[1];
    }
  }
  
  // For Date objects, convert to Adelaide timezone
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  return date.toLocaleDateString('en-CA', { timeZone: ADELAIDE_TIMEZONE });
};

/**
 * Formats date for display in Adelaide timezone
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    timeZone: ADELAIDE_TIMEZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Formats time for display in Adelaide timezone
 */
export const formatTime = (dateString) => {
  if (!dateString) return '';
  // Parse the raw time from the stored string (already in Adelaide time)
  const match = dateString.match(/T(\d{2}):(\d{2})/);
  if (match) {
    let h = parseInt(match[1], 10);
    const m = match[2];
    const ampm = h >= 12 ? 'pm' : 'am';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return m === '00' ? h12 + ampm : h12 + ':' + m + ampm;
  }
  // Fallback
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  return `${formatDate(dateString)} ${formatTime(dateString)}`;
};

/**
 * Formats date for datetime-local input in Adelaide timezone
 * Returns YYYY-MM-DDTHH:mm format
 */
export const formatDateTimeLocal = (dateInput) => {
  if (!dateInput) return '';
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  // Get date parts in Adelaide timezone
  const options = { timeZone: ADELAIDE_TIMEZONE, hour12: false };
  const year = date.toLocaleDateString('en-CA', { ...options, year: 'numeric' }).split('/')[0] || 
               date.toLocaleDateString('en-CA', { timeZone: ADELAIDE_TIMEZONE }).split('-')[0];
  const month = String(new Date(date.toLocaleString('en-US', { timeZone: ADELAIDE_TIMEZONE })).getMonth() + 1).padStart(2, '0');
  const day = String(new Date(date.toLocaleString('en-US', { timeZone: ADELAIDE_TIMEZONE })).getDate()).padStart(2, '0');
  const hours = String(new Date(date.toLocaleString('en-US', { timeZone: ADELAIDE_TIMEZONE })).getHours()).padStart(2, '0');
  const minutes = String(new Date(date.toLocaleString('en-US', { timeZone: ADELAIDE_TIMEZONE })).getMinutes()).padStart(2, '0');
  
  // Use a simpler approach - convert to Adelaide time string and parse
  const adelaideStr = date.toLocaleString('en-AU', { 
    timeZone: ADELAIDE_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  // Parse "DD/MM/YYYY, HH:mm" format
  const parts = adelaideStr.match(/(\d{2})\/(\d{2})\/(\d{4}),?\s*(\d{2}):(\d{2})/);
  if (parts) {
    return `${parts[3]}-${parts[2]}-${parts[1]}T${parts[4]}:${parts[5]}`;
  }
  
  // Fallback to local time if parsing fails
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const getShiftTypeClass = (shiftType) => {
  const type = shiftType?.toLowerCase().replace(/\s+/g, '-');
  return `shift-${type}`;
};

export const getStatusClass = (status) => {
  return `status-${status?.toLowerCase().replace(/\s+/g, '_')}`;
};

export const getRoleThemeClass = (role) => {
  const themes = {
    admin: 'theme-admin',
    house_manager: 'theme-manager',
    worker: 'theme-worker',
    family: 'theme-family'
  };
  return themes[role] || 'theme-admin';
};

export const getRoleColor = (role) => {
  const colors = {
    admin: 'hsl(262, 83%, 58%)',
    house_manager: 'hsl(173, 58%, 39%)',
    worker: 'hsl(32, 95%, 44%)',
    family: 'hsl(330, 81%, 60%)'
  };
  return colors[role] || colors.admin;
};

export const getRoleName = (role) => {
  const names = {
    admin: 'Admin',
    house_manager: 'House Manager',
    worker: 'Worker',
    family: 'Family/Coordinator'
  };
  return names[role] || role;
};

/**
 * Generates a date range starting from the given date, normalized to Adelaide timezone.
 * Returns an array of Date objects representing each day.
 */
export const generateDateRange = (startDate, days = 14) => {
  const dates = [];
  
  // Get today's date in Adelaide as YYYY-MM-DD
  const startDateStr = toAdelaideDateString(startDate);
  const [year, month, day] = startDateStr.split('-').map(Number);
  
  for (let i = 0; i < days; i++) {
    // Create a date at noon to avoid DST edge cases
    const date = new Date(year, month - 1, day + i, 12, 0, 0, 0);
    dates.push(date);
  }
  
  return dates;
};

/**
 * Checks if a date is today in Adelaide timezone
 */
export const isToday = (date) => {
  const todayAdelaide = toAdelaideDateString(new Date());
  const dateAdelaide = toAdelaideDateString(date);
  return todayAdelaide === dateAdelaide;
};

export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};
